import {
  getRelativeFontSize,
  mediumFont,
  regularFont,
  theme,
} from "../../utils/styles";

const homeStyles = {
  // ── Root wrapper ───────────────────────────────────────────────────────────
  pageWrapper: {
    width: "100%",
    minHeight: "100vh",
    background: "#060612",
    position: "relative",
    overflowX: "hidden",
  },

  // ── Animated aurora layer (fixed) ──────────────────────────────────────────
  auroraWrap: {
    position: "fixed",
    inset: 0,
    zIndex: 0,
    pointerEvents: "none",
    overflow: "hidden",
  },
  auroraA: {
    position: "absolute",
    width: { xl: "900px", md: "600px", xs: "320px" },
    height: { xl: "900px", md: "600px", xs: "320px" },
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(110,50,210,0.36) 0%, transparent 68%)",
    top: "-200px",
    left: "-200px",
    filter: "blur(90px)",
    animation: "auroraA 14s ease-in-out infinite",
    "@keyframes auroraA": {
      "0%,100%": { transform: "translate(0,0) scale(1)" },
      "33%": { transform: "translate(120px,90px) scale(1.12)" },
      "66%": { transform: "translate(-40px,130px) scale(0.94)" },
    },
  },
  auroraB: {
    position: "absolute",
    width: { xl: "700px", md: "480px", xs: "260px" },
    height: { xl: "700px", md: "480px", xs: "260px" },
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(70,80,220,0.26) 0%, transparent 68%)",
    top: "30%",
    right: "-160px",
    filter: "blur(100px)",
    animation: "auroraB 19s ease-in-out infinite",
    "@keyframes auroraB": {
      "0%,100%": { transform: "translate(0,0) scale(1)" },
      "50%": { transform: "translate(-130px,-110px) scale(1.18)" },
    },
  },
  auroraC: {
    position: "absolute",
    width: { xl: "500px", md: "340px", xs: "200px" },
    height: { xl: "500px", md: "340px", xs: "200px" },
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(6,182,212,0.17) 0%, transparent 68%)",
    bottom: "12%",
    left: "35%",
    filter: "blur(80px)",
    animation: "auroraC 24s ease-in-out infinite",
    "@keyframes auroraC": {
      "0%,100%": { transform: "translate(0,0) scale(1)" },
      "40%": { transform: "translate(80px,-70px) scale(1.1)" },
      "70%": { transform: "translate(-30px,50px) scale(0.9)" },
    },
  },

  // ── Hero section ───────────────────────────────────────────────────────────
  heroSection: {
    position: "relative",
    zIndex: 1,
    pt: { xl: "130px", lg: "110px", md: "100px", sm: "140px", xs: "140px" },
    pb: 0,
    minHeight: { md: "80vh", xs: "auto" },
    display: "flex",
    alignItems: "center",
  },

  // Dot-grid texture overlay
  heroDotGrid: {
    position: "absolute",
    inset: 0,
    backgroundImage:
      "radial-gradient(rgba(255,255,255,0.065) 1px, transparent 1px)",
    backgroundSize: "38px 38px",
    maskImage:
      "radial-gradient(ellipse 85% 65% at 50% 38%, black 20%, transparent 100%)",
    WebkitMaskImage:
      "radial-gradient(ellipse 85% 65% at 50% 38%, black 20%, transparent 100%)",
    pointerEvents: "none",
  },

  // Badge
  heroBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 1,
    px: 2,
    py: 0.75,
    mb: 2.5,
    borderRadius: "100px",
    background: "rgba(144,83,246,0.1)",
    border: "1px solid rgba(144,83,246,0.38)",
    backdropFilter: "blur(12px)",
    color: "#C4B5FD",
    ...regularFont,
    fontSize: getRelativeFontSize(-1),
    letterSpacing: "0.07em",
    width: "fit-content",
    animation: "badgePulse 3.5s ease-in-out infinite",
    "@keyframes badgePulse": {
      "0%,100%": { boxShadow: "0 0 0 rgba(144,83,246,0)" },
      "50%": { boxShadow: "0 0 20px rgba(144,83,246,0.38)" },
    },
  },

  heroTagline: {
    ...regularFont,
    color: "rgba(196,181,253,0.65)",
    fontSize: getRelativeFontSize(-1),
    letterSpacing: "0.13em",
    textTransform: "uppercase",
    mb: 1.5,
  },

  // Large gradient headline
  heroHeadline: {
    ...mediumFont,
    fontSize: { xl: "70px", lg: "56px", md: "44px", sm: "34px", xs: "28px" },
    lineHeight: 1.04,
    letterSpacing: "-0.03em",
    mb: 2.5,
    background:
      "linear-gradient(128deg, #FFFFFF 0%, #EDE9FE 28%, #A78BFA 52%, #6366F1 72%, #22D3EE 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
  },

  heroSubtitle: {
    ...regularFont,
    color: "rgba(255,255,255,0.46)",
    fontSize: { md: getRelativeFontSize(2), xs: getRelativeFontSize(0) },
    lineHeight: 1.82,
    mb: 4,
    maxWidth: "460px",
  },

  // Social proof row — avatars + count text
  heroSocialProof: {
    display: "flex",
    alignItems: "center",
    gap: 1.5,
    mb: 4,
  },
  socialAvatarStack: {
    display: "flex",
    alignItems: "center",
  },
  socialProofText: {
    ...regularFont,
    color: "rgba(255,255,255,0.5)",
    fontSize: getRelativeFontSize(-1),
    "& strong": { color: "#C4B5FD", fontWeight: 600 },
  },

  ctaRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 2,
    alignItems: "center",
  },

  primaryBtn: {
    py: { md: 1.5, xs: 1.25 },
    px: { md: 4.5, xs: 3 },
    borderRadius: "12px",
    background: "linear-gradient(135deg, #9053F6 0%, #6366F1 100%)",
    color: "#FFFFFF",
    ...mediumFont,
    fontSize: getRelativeFontSize(2),
    letterSpacing: "0.01em",
    minWidth: "148px",
    position: "relative",
    overflow: "hidden",
    transition: "transform 0.22s ease, box-shadow 0.22s ease",
    "&::after": {
      content: '""',
      position: "absolute",
      top: 0,
      left: "-100%",
      width: "60%",
      height: "100%",
      background:
        "linear-gradient(90deg, transparent, rgba(255,255,255,0.22), transparent)",
      transition: "left 0.5s ease",
    },
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 0 44px rgba(144,83,246,0.65), 0 8px 22px rgba(0,0,0,0.35)",
      "&::after": { left: "160%" },
    },
    "&:active": { transform: "translateY(0px)" },
  },

  secondaryBtn: {
    py: { md: 1.5, xs: 1.25 },
    px: { md: 4.5, xs: 3 },
    borderRadius: "12px",
    background: "rgba(255,255,255,0.04)",
    color: "rgba(255,255,255,0.82)",
    border: "1px solid rgba(255,255,255,0.15)",
    ...mediumFont,
    fontSize: getRelativeFontSize(2),
    minWidth: "148px",
    backdropFilter: "blur(16px)",
    transition: "all 0.22s ease",
    "&:hover": {
      background: "rgba(144,83,246,0.14)",
      borderColor: "rgba(144,83,246,0.5)",
      color: "#FFFFFF",
      transform: "translateY(-2px)",
      boxShadow: "0 8px 22px rgba(0,0,0,0.3)",
    },
    "&:active": { transform: "translateY(0px)" },
  },

  // ── Hero image column ───────────────────────────────────────────────────────
  heroImageCol: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    [theme.breakpoints.down("md")]: { mt: 6 },
  },

  // Wrapper that holds the image + all decorations with shared reference point
  heroImageWrapper: {
    position: "relative",
    width: "100%",
    maxWidth: { xl: "560px", lg: "480px", md: "400px", xs: "340px" },
    mx: "auto",
  },

  heroGlowRing: {
    position: "absolute",
    inset: "-8%",
    borderRadius: "50%",
    background:
      "radial-gradient(circle, rgba(144,83,246,0.32) 0%, rgba(99,102,241,0.1) 50%, transparent 72%)",
    filter: "blur(55px)",
    animation: "ringPulse 5s ease-in-out infinite",
    "@keyframes ringPulse": {
      "0%,100%": { opacity: 0.7, transform: "scale(1)" },
      "50%": { opacity: 1, transform: "scale(1.07)" },
    },
  },

  heroOrbitRing1: {
    position: "absolute",
    inset: "-12%",
    borderRadius: "50%",
    border: "1px dashed rgba(144,83,246,0.22)",
    animation: "spin1 28s linear infinite",
    "@keyframes spin1": {
      from: { transform: "rotate(0deg)" },
      to: { transform: "rotate(360deg)" },
    },
    "&::before": {
      content: '""',
      position: "absolute",
      top: "-5px",
      left: "50%",
      width: "10px",
      height: "10px",
      borderRadius: "50%",
      background: "#9053F6",
      boxShadow: "0 0 14px 5px rgba(144,83,246,0.7)",
      transform: "translateX(-50%)",
    },
  },

  heroOrbitRing2: {
    position: "absolute",
    inset: "6%",
    borderRadius: "50%",
    border: "1px dashed rgba(99,102,241,0.16)",
    animation: "spin2 20s linear infinite reverse",
    "@keyframes spin2": {
      from: { transform: "rotate(0deg)" },
      to: { transform: "rotate(360deg)" },
    },
    "&::before": {
      content: '""',
      position: "absolute",
      bottom: "-4px",
      left: "50%",
      width: "8px",
      height: "8px",
      borderRadius: "50%",
      background: "#22D3EE",
      boxShadow: "0 0 10px 4px rgba(34,211,238,0.6)",
      transform: "translateX(-50%)",
    },
  },

  heroImageBox: {
    position: "relative",
    zIndex: 2,
    width: "100%",
    animation: "heroFloat 8s ease-in-out infinite",
    filter:
      "drop-shadow(0 0 48px rgba(144,83,246,0.55)) drop-shadow(0 24px 64px rgba(0,0,0,0.5))",
    "@keyframes heroFloat": {
      "0%": { transform: "translateY(0px)" },
      "50%": { transform: "translateY(-20px)" },
      "100%": { transform: "translateY(0px)" },
    },
  },

  // Floating info chips (only on lg/xl)
  floatingChipBase: {
    position: "absolute",
    display: { xl: "flex", lg: "flex", md: "none", sm: "none", xs: "none" },
    alignItems: "center",
    gap: 0.75,
    px: 1.5,
    py: 0.75,
    borderRadius: "12px",
    background: "rgba(10,8,30,0.75)",
    border: "1px solid rgba(255,255,255,0.12)",
    backdropFilter: "blur(18px)",
    WebkitBackdropFilter: "blur(18px)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
    zIndex: 3,
    animation: "chipFloat 6s ease-in-out infinite",
    "@keyframes chipFloat": {
      "0%,100%": { transform: "translateY(0px)" },
      "50%": { transform: "translateY(-6px)" },
    },
  },

  floatingChip1: { top: "10%", left: "4px", animationDelay: "0s" },
  floatingChip2: { top: "6%", right: "4px", animationDelay: "1.5s" },
  floatingChip3: { bottom: "30%", left: "4px", animationDelay: "3s" },
  floatingChip4: { bottom: "8%", right: "4px", animationDelay: "4.5s" },

  chipSymbol: {
    fontSize: "18px",
    lineHeight: 1,
  },

  chipText: {
    ...mediumFont,
    color: "#FFFFFF",
    fontSize: getRelativeFontSize(-2),
    whiteSpace: "nowrap",
  },

  chipSubText: {
    ...regularFont,
    color: "rgba(255,255,255,0.48)",
    fontSize: getRelativeFontSize(-4),
    whiteSpace: "nowrap",
  },

  // ── Stats strip ─────────────────────────────────────────────────────────────
  statsStrip: {
    position: "relative",
    zIndex: 1,
    borderTop: "1px solid rgba(255,255,255,0.05)",
    borderBottom: "1px solid rgba(255,255,255,0.05)",
    background: "rgba(255,255,255,0.018)",
    backdropFilter: "blur(24px)",
    py: { md: 4, xs: 3 },
  },

  statsInner: {
    display: "grid",
    gridTemplateColumns: { md: "repeat(4,1fr)", xs: "repeat(2,1fr)" },
    gap: { md: 0, xs: 2 },
  },

  statItem: {
    textAlign: "center",
    px: { md: 3, xs: 1 },
    borderRight: "1px solid rgba(255,255,255,0.06)",
    "&:last-child": { borderRight: "none" },
    [theme.breakpoints.down("sm")]: {
      "&:nth-of-type(2n)": { borderRight: "none" },
      "&:nth-of-type(3)": { borderRight: "1px solid rgba(255,255,255,0.06)" },
    },
  },

  statNumber: {
    ...mediumFont,
    fontSize: {
      md: getRelativeFontSize(13),
      sm: getRelativeFontSize(10),
      xs: getRelativeFontSize(8),
    },
    background:
      "linear-gradient(135deg, #FFFFFF 10%, #C4B5FD 55%, #9053F6 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    lineHeight: 1.15,
  },

  statLabel: {
    ...regularFont,
    color: "rgba(255,255,255,0.38)",
    fontSize: { md: getRelativeFontSize(-1), xs: getRelativeFontSize(-3) },
    mt: 0.5,
    letterSpacing: "0.04em",
  },

  // ── Features section ─────────────────────────────────────────────────────────
  featuresSection: {
    position: "relative",
    zIndex: 1,
    py: { xl: 11, lg: 10, md: 9, sm: 7, xs: 5 },
  },

  sectionHeadingBox: {
    textAlign: "center",
    mb: { md: 5.5, xs: 4 },
  },

  sectionEyebrow: {
    ...mediumFont,
    fontSize: getRelativeFontSize(-2),
    color: "#A78BFA",
    letterSpacing: "0.18em",
    textTransform: "uppercase",
    mb: 1.5,
  },

  sectionTitle: {
    ...mediumFont,
    fontSize: { xl: "46px", lg: "38px", md: "32px", sm: "24px", xs: "22px" },
    lineHeight: 1.12,
    mb: 1.5,
    background:
      "linear-gradient(135deg, #FFFFFF 40%, #C4B5FD 70%, #9053F6 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
  },

  sectionSubtitle: {
    ...regularFont,
    color: "rgba(255,255,255,0.42)",
    fontSize: { md: getRelativeFontSize(1), xs: getRelativeFontSize(-1) },
    lineHeight: 1.72,
    maxWidth: "520px",
    mx: "auto",
  },

  // Category pills
  tabsWrapper: {
    display: "flex",
    flexWrap: "wrap",
    gap: { md: 1.25, xs: 0.875 },
    justifyContent: "center",
    mb: { md: 5.5, xs: 3.5 },
  },

  tabPillBase: {
    display: "inline-flex",
    alignItems: "center",
    px: { md: 2.5, xs: 1.75 },
    py: { md: 0.875, xs: 0.625 },
    borderRadius: "100px",
    cursor: "pointer",
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.08)",
    color: "rgba(255,255,255,0.46)",
    ...regularFont,
    fontSize: { md: getRelativeFontSize(0), xs: getRelativeFontSize(-2) },
    backdropFilter: "blur(10px)",
    transition: "all 0.22s ease",
    userSelect: "none",
    "&:hover": {
      background: "rgba(144,83,246,0.15)",
      borderColor: "rgba(144,83,246,0.4)",
      color: "rgba(255,255,255,0.85)",
    },
  },

  tabPillActive: {
    background:
      "linear-gradient(135deg, rgba(144,83,246,0.38), rgba(99,102,241,0.26))",
    borderColor: "rgba(144,83,246,0.65)",
    color: "#FFFFFF",
    boxShadow: "0 0 22px rgba(144,83,246,0.32)",
  },

  // ── Feature card — vertical, full description visible ──────────────────────
  featureCard: {
    p: { md: 3, sm: 2.5, xs: 2 },
    borderRadius: "20px",
    background: "rgba(255,255,255,0.025)",
    border: "1px solid rgba(255,255,255,0.07)",
    backdropFilter: "blur(20px)",
    WebkitBackdropFilter: "blur(20px)",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    position: "relative",
    overflow: "hidden",
    transition:
      "transform 0.28s ease, box-shadow 0.28s ease, border-color 0.28s ease, background 0.28s ease",
    zIndex: 0,
    // Top accent line — color injected inline per card
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      height: "2px",
      opacity: 0.7,
      transition: "opacity 0.28s ease",
    },
    "&:hover": {
      transform: "translateY(-5px)",
      zIndex: 2,
      borderColor: "rgba(255,255,255,0.14)",
      boxShadow:
        "0 16px 48px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.06) inset",
      "&::before": { opacity: 1 },
    },
  },

  featureIconCircle: {
    width: { md: "52px", sm: "44px", xs: "40px" },
    height: { md: "52px", sm: "44px", xs: "40px" },
    borderRadius: "16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    mb: { md: 2.5, xs: 2 },
    flexShrink: 0,
    position: "relative",
    "&::after": {
      content: '""',
      position: "absolute",
      inset: "-4px",
      borderRadius: "20px",
      opacity: 0.3,
      filter: "blur(10px)",
      // background injected inline
    },
  },

  featureCategoryBadge: {
    display: "inline-flex",
    alignItems: "center",
    px: 1.25,
    py: 0.3,
    borderRadius: "100px",
    ...regularFont,
    fontSize: getRelativeFontSize(-4),
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    mb: 1,
    // bg + color injected inline
  },

  featureCardTitle: {
    ...mediumFont,
    color: "#F1EDFF",
    fontSize: {
      md: getRelativeFontSize(2),
      sm: getRelativeFontSize(1),
      xs: getRelativeFontSize(0),
    },
    lineHeight: 1.3,
    mb: 1,
  },

  featureCardDesc: {
    ...regularFont,
    color: "rgba(255,255,255,0.44)",
    fontSize: {
      md: getRelativeFontSize(0),
      sm: getRelativeFontSize(-1),
      xs: getRelativeFontSize(-2),
    },
    lineHeight: 1.65,
    flex: 1,
  },

  /* ══════════════════════════════════════════════════════════════════════════
   * OPTION A (commented out) — Infinite marquee strips + spotlight panel
   * ══════════════════════════════════════════════════════════════════════════

  featuresMarqueeWrap: {
    position: "relative", overflow: "hidden",
    maskImage: "linear-gradient(90deg, transparent 0%, black 8%, black 92%, transparent 100%)",
    WebkitMaskImage: "linear-gradient(90deg, transparent 0%, black 8%, black 92%, transparent 100%)",
    py: { md: 1, xs: 0.5 },
  },
  marqueeStrip: { py: { md: "10px", xs: "8px" }, "&:hover .mTrack": { animationPlayState: "paused" } },
  marqueeTrackLeft: { display: "flex", width: "fit-content", gap: { md: "14px", xs: "10px" }, animation: "mLeft 50s linear infinite", "@keyframes mLeft": { "0%": { transform: "translateX(0)" }, "100%": { transform: "translateX(-50%)" } } },
  marqueeTrackRight: { display: "flex", width: "fit-content", gap: { md: "14px", xs: "10px" }, animation: "mRight 42s linear infinite", "@keyframes mRight": { "0%": { transform: "translateX(-50%)" }, "100%": { transform: "translateX(0)" } } },
  marqueePill: { display: "flex", alignItems: "center", gap: "10px", px: { md: "18px", xs: "14px" }, py: { md: "11px", xs: "9px" }, borderRadius: "100px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", backdropFilter: "blur(14px)", flexShrink: 0, cursor: "default", userSelect: "none", transition: "background 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease, transform 0.25s ease" },
  pillIconDot: { width: { md: "30px", xs: "24px" }, height: { md: "30px", xs: "24px" }, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  pillName: { ...mediumFont, color: "#EDE9FE", fontSize: { md: getRelativeFontSize(1), xs: getRelativeFontSize(-1) }, whiteSpace: "nowrap" as const },
  pillSep: { width: "3px", height: "3px", borderRadius: "50%", background: "rgba(255,255,255,0.18)", flexShrink: 0, display: { md: "block", xs: "none" } },
  pillCategory: { ...regularFont, fontSize: getRelativeFontSize(-3), letterSpacing: "0.07em", textTransform: "uppercase" as const, whiteSpace: "nowrap" as const, display: { md: "block", xs: "none" } },
  featureSpotlight: { mt: { md: 5, xs: 4 }, p: { md: "36px", sm: "24px", xs: "20px" }, borderRadius: "24px", display: "flex", flexDirection: { md: "row", xs: "column" }, alignItems: { md: "center", xs: "flex-start" }, gap: { md: "36px", xs: "20px" }, position: "relative", overflow: "hidden", backdropFilter: "blur(30px)", transition: "border-color 0.4s ease, background 0.4s ease", "&::before": { content: '""', position: "absolute", top: 0, left: 0, bottom: 0, width: "4px", borderRadius: "4px 0 0 4px" }, "&::after": { content: '""', position: "absolute", inset: 0, borderRadius: "24px", pointerEvents: "none" } },
  spotlightIconWrap: { width: { md: "88px", sm: "64px", xs: "56px" }, height: { md: "88px", sm: "64px", xs: "56px" }, borderRadius: "22px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, position: "relative", transition: "all 0.4s ease" },
  spotlightContent: { flex: 1, minWidth: 0, position: "relative", zIndex: 1 },
  spotlightCategory: { display: "inline-flex", alignItems: "center", px: 1.5, py: 0.4, borderRadius: "100px", ...regularFont, fontSize: getRelativeFontSize(-3), letterSpacing: "0.08em", textTransform: "uppercase" as const, mb: 1.5 },
  spotlightTitle: { ...mediumFont, color: "#F1EDFF", fontSize: { md: getRelativeFontSize(8), sm: getRelativeFontSize(5), xs: getRelativeFontSize(3) }, lineHeight: 1.12, mb: 1.5, transition: "color 0.35s ease" },
  spotlightDesc: { ...regularFont, color: "rgba(255,255,255,0.52)", fontSize: { md: getRelativeFontSize(1), xs: getRelativeFontSize(-1) }, lineHeight: 1.75 },
  spotlightBgNumber: { position: "absolute", right: { md: "32px", xs: "16px" }, top: "50%", transform: "translateY(-50%)", fontSize: { md: "120px", xs: "72px" }, lineHeight: 1, opacity: 0.04, userSelect: "none", pointerEvents: "none", ...mediumFont, color: "#FFFFFF" },

   * ══════════════════════════════════════════════════════════════════════════ */

  /* ══════════════════════════════════════════════════════════════════════════
   * OPTION B (commented out) — Constellation orrery wheel
   * Left: circular star-map with 13 nodes + rotating orbit rings.
   * Right: editorial detail panel updates on node click.
   * Problem: user must interact to see all features — bad UX.
   * Styles: constellWrap, wheelBox, wheelRing1/2, wheelCenterGlow/Orb,
   *         wheelNode, wheelDetail, wheelDetailCategory/Title/Desc,
   *         wheelNavDots/Dot
   * ══════════════════════════════════════════════════════════════════════════ */

  /* ══════════════════════════════════════════════════════════════════════════
   * OPTION C (commented out) — Stacked glowing reveal strips
   * 13 full-width horizontal rows. Timeline spine on left, number dot,
   * icon badge, name + full description, category pill on right.
   * All content visible. Alternating row tints. Hover tints row to feature color.
   * Styles: featureStripList, featureStrip, featureSpineDot, spineDotNumber,
   *         featureStripIconBadge, featureStripText, featureStripName,
   *         featureStripDesc, featureStripCat
   * ══════════════════════════════════════════════════════════════════════════ */

  // ── OPTION D: Celestial Columns — proportional category pillars ────────────
  // 5 vertical columns, one per category, width proportional to feature count
  // (3fr · 4fr · 2fr · 2fr · 2fr = 13 features total). The COLUMN is the
  // visual container — no card borders on individual feature items.
  // Each column: top glow bar (category color) + zodiac symbol header +
  // category name + feature count + divider + feature list (icon + name + desc).
  // ALL content visible at first glance. Hover enhances, never reveals.

  // Outer scroll wrapper — allows horizontal pan on sm/xs without breaking layout
  featureCelestialWrap: {
    overflowX: { lg: "visible", md: "auto", xs: "auto" },
    // Horizontal scroll hint on mobile
    WebkitOverflowScrolling: "touch",
    pb: { md: 0, xs: "4px" },
  },

  // The 5-column proportional grid
  featureCelestialGrid: {
    display: "grid",
    gridTemplateColumns: {
      lg: "3fr 4fr 2fr 2fr 2fr",
      md: "3fr 4fr 2fr 2fr 2fr",
    },
    minWidth: { lg: "unset", md: "800px", xs: "760px" },
    borderRadius: "20px",
    overflow: "hidden",
    border: "1px solid rgba(255,255,255,0.07)",
    background: "rgba(255,255,255,0.01)",
  },

  // One category column/pillar
  featureCelestialCol: {
    px: { md: "22px", xs: "16px" },
    pt: { md: "28px", xs: "20px" },
    pb: { md: "32px", xs: "24px" },
    borderRight: "1px solid rgba(255,255,255,0.06)",
    "&:last-child": { borderRight: "none" },
    position: "relative",
    transition: "background 0.35s ease",
    // 3px top accent bar — color injected inline
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      height: "3px",
      // background + boxShadow injected inline
    },
    // Top radial glow — color injected inline
    "&::after": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      height: "160px",
      pointerEvents: "none",
      // background injected inline
    },
    "&:hover": {
      // background injected inline per column
    },
  },

  // Category header block — zodiac symbol + name + count
  featureCelestialCatHeader: {
    mb: "22px",
    position: "relative",
  },

  // Huge faded ordinal behind the header (01, 02, 03…) — decorative only
  featureCelestialBgNum: {
    position: "absolute",
    top: "-6px",
    right: "-4px",
    fontSize: { md: "64px", xs: "48px" },
    lineHeight: 1,
    opacity: 0.045,
    color: "#FFFFFF",
    userSelect: "none",
    pointerEvents: "none",
    ...mediumFont,
  },

  // Square icon container for the zodiac/category symbol
  featureCelestialCatIcon: {
    width: "38px",
    height: "38px",
    borderRadius: "11px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    mb: "10px",
    // bg + border injected inline
  },

  featureCelestialCatSymbol: {
    fontSize: "20px",
    lineHeight: 1,
    userSelect: "none",
  },

  featureCelestialCatName: {
    ...mediumFont,
    fontSize: getRelativeFontSize(-1),
    letterSpacing: "0.06em",
    lineHeight: 1.2,
    mb: "3px",
    // color injected inline
  },

  featureCelestialCatCount: {
    ...regularFont,
    fontSize: getRelativeFontSize(-4),
    color: "rgba(255,255,255,0.22)",
    letterSpacing: "0.05em",
  },

  // Gradient hairline divider below header
  featureCelestialDivider: {
    height: "1px",
    mb: "20px",
    // background injected inline (category color → transparent)
  },

  // A single feature row inside the column — NOT a card (no border, no bg)
  featureCelestialItem: {
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
    pb: "16px",
    mb: "16px",
    borderBottom: "1px solid rgba(255,255,255,0.04)",
    "&:last-child": { borderBottom: "none", pb: 0, mb: 0 },
    transition: "transform 0.2s ease",
    "&:hover": { transform: "translateX(4px)" },
  },

  // Small square icon badge per feature — smaller than the category icon
  featureCelestialItemIcon: {
    width: { md: "34px", xs: "28px" },
    height: { md: "34px", xs: "28px" },
    borderRadius: "9px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    mt: "1px",
    transition: "box-shadow 0.25s ease",
    // bg + border injected inline
  },

  featureCelestialItemName: {
    ...mediumFont,
    color: "#F1EDFF",
    fontSize: { md: getRelativeFontSize(1), xs: getRelativeFontSize(0) },
    lineHeight: 1.2,
    mb: "5px",
  },

  featureCelestialItemDesc: {
    ...regularFont,
    color: "rgba(255,255,255,0.36)",
    fontSize: { md: getRelativeFontSize(-1), xs: getRelativeFontSize(-2) },
    lineHeight: 1.62,
  },

  /* ══════════════════════════════════════════════════════════════════════════
   * OPTION D (commented out) — Celestial Columns
   * 5 proportional pillars (3fr·4fr·2fr·2fr·2fr) — column width = feature count.
   * Column is the visual container. All content visible. Category header with
   * zodiac symbol + glow bar top. Hover tints column.
   * Styles: featureCelestialWrap, featureCelestialGrid, featureCelestialCol,
   *         featureCelestialCatHeader, featureCelestialBgNum, featureCelestialCatIcon,
   *         featureCelestialCatSymbol/Name/Count/Divider, featureCelestialItem,
   *         featureCelestialItemIcon/Name/Desc
   * ══════════════════════════════════════════════════════════════════════════ */

  // ── OPTION E: Spectral Crystal Bands ──────────────────────────────────────
  // Each category is a full-width diagonal slab — container is skewY(-2deg),
  // inner content counter-skewed so text is flat. Slabs overlap by 40px with
  // later bands on top, creating visible diagonal seam lines (crystal fractures).
  // Left panel: large zodiac glyph + category name + count.
  // Right panel: features in 2-col flex — icon, title, full description.
  // ALL content visible. Hover enhances glow only. Works on mobile via stack.

  // Clips the overflowing corners of the skewed bands
  featureSpectraWrap: {
    position: "relative",
    overflow: "hidden",
    mt: { md: "40px", xs: "28px" },
    mb: { md: "40px", xs: "28px" },
  },

  // The skewed band — the transform tilts the whole slab
  featureSpectraBand: {
    transform: "skewY(-2deg)",
    transformOrigin: "0 0",
    mt: { md: "-40px", xs: "-28px" },
    position: "relative",
  },

  // Counter-skew so text/content stays perfectly horizontal
  featureSpectraBandInner: {
    transform: "skewY(2deg)",
    transformOrigin: "0 0",
    pt: { md: "72px", sm: "52px", xs: "40px" },
    pb: { md: "72px", sm: "52px", xs: "40px" },
    display: "grid",
    gridTemplateColumns: { md: "200px 1fr", sm: "140px 1fr", xs: "1fr" },
    gap: { md: "56px", sm: "28px", xs: "24px" },
    alignItems: "center",
    maxWidth: "1400px",
    mx: "auto",
    px: { xl: "64px", lg: "40px", md: "28px", sm: "20px", xs: "16px" },
  },

  // Left panel — category identity
  featureSpectraCatPanel: {
    pt: { md: "6px", xs: 0 },
    position: "relative",
    [theme.breakpoints.down("lg")]: {
      justifyItems: "center",
    },
  },

  // Large zodiac glyph — the cosmic anchor of each slab
  featureSpectraCatGlyph: {
    fontSize: { md: "72px", sm: "52px", xs: "44px" },
    lineHeight: 1,
    mb: "12px",
    display: "block",
    userSelect: "none",
    animation: "glyphBreath 5s ease-in-out infinite",
    "@keyframes glyphBreath": {
      "0%,100%": { opacity: 0.75, filter: "drop-shadow(0 0 6px currentColor)" },
      "50%": { opacity: 1, filter: "drop-shadow(0 0 22px currentColor)" },
    },
  },

  featureSpectraCatName: {
    ...mediumFont,
    fontSize: { md: getRelativeFontSize(1), xs: getRelativeFontSize(0) },
    letterSpacing: "0.1em",
    textTransform: "uppercase" as const,
    mb: "4px",
  },

  featureSpectraCatCount: {
    ...regularFont,
    fontSize: getRelativeFontSize(-3),
    color: "rgba(255,255,255,0.24)",
    letterSpacing: "0.04em",
    mb: "18px",
  },

  featureSpectraCatRule: {
    width: "24px",
    height: "2px",
    borderRadius: "1px",
  },

  // Right panel — features in a 2-col flex
  featureSpectraFeatures: {
    display: "flex",
    flexWrap: "wrap" as const,
    gap: { md: "28px 24px", xs: "20px" },
  },

  // One feature item — icon + text stack, always fully visible
  featureSpectraItem: {
    flex: {
      md: "0 0 calc(50% - 12px)",
      sm: "0 0 calc(50% - 10px)",
      xs: "0 0 100%",
    },
    display: "flex",
    alignItems: "flex-start",
    gap: { md: "14px", xs: "12px" },
    transition: "transform 0.25s ease",
    "&:hover": { transform: "translateX(5px)" },
  },

  featureSpectraIcon: {
    width: { md: "46px", xs: "38px" },
    height: { md: "46px", xs: "38px" },
    borderRadius: "13px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    mt: "2px",
    transition: "box-shadow 0.3s ease, transform 0.3s ease",
    "&:hover": { transform: "scale(1.1) rotate(-6deg)" },
  },

  featureSpectraName: {
    ...mediumFont,
    color: "#F1EDFF",
    fontSize: {
      md: getRelativeFontSize(3),
      sm: getRelativeFontSize(2),
      xs: getRelativeFontSize(1),
    },
    lineHeight: 1.18,
    mb: "7px",
  },

  featureSpectraDesc: {
    ...regularFont,
    color: "rgba(255,255,255,0.42)",
    fontSize: { md: getRelativeFontSize(0), xs: getRelativeFontSize(-1) },
    lineHeight: 1.68,
  },

  // ── Bottom CTA section ──────────────────────────────────────────────────────
  bottomSection: {
    position: "relative",
    zIndex: 1,
    py: { xl: 11, lg: 10, md: 9, sm: 7, xs: 5 },
    px: 0,
  },

  // Zodiac symbols decorative row
  zodiacRow: {
    textAlign: "center",
    mb: 3.5,
    letterSpacing: { md: "0.9em", xs: "0.4em" },
    fontSize: { md: "20px", xs: "16px" },
    color: "rgba(255,255,255,0.1)",
    userSelect: "none",
  },

  bottomCard: {
    borderRadius: "28px",
    background:
      "linear-gradient(140deg, rgba(144,83,246,0.1) 0%, rgba(99,102,241,0.07) 45%, rgba(6,182,212,0.05) 100%)",
    border: "1px solid rgba(144,83,246,0.2)",
    p: { xl: 8, lg: 7, md: 6, sm: 5, xs: 3.5 },
    textAlign: "center",
    maxWidth: "880px",
    mx: "auto",
    backdropFilter: "blur(30px)",
    WebkitBackdropFilter: "blur(30px)",
    position: "relative",
    overflow: "hidden",
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      height: "1px",
      background:
        "linear-gradient(90deg, transparent 0%, rgba(144,83,246,0.7) 30%, rgba(99,102,241,0.7) 60%, rgba(6,182,212,0.5) 85%, transparent 100%)",
    },
    "&::after": {
      content: '""',
      position: "absolute",
      inset: 0,
      borderRadius: "28px",
      background:
        "radial-gradient(ellipse 70% 45% at 50% 0%, rgba(144,83,246,0.14) 0%, transparent 65%)",
      pointerEvents: "none",
    },
  },

  // 3 benefit items row inside bottom card
  benefitRow: {
    display: "flex",
    justifyContent: "center",
    gap: { md: 5, sm: 3, xs: 2 },
    mb: { md: 4, xs: 3 },
    position: "relative",
    zIndex: 1,
    flexWrap: "wrap",
  },

  benefitItem: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 0.75,
  },

  benefitIconBox: {
    width: "44px",
    height: "44px",
    borderRadius: "14px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "rgba(144,83,246,0.15)",
    border: "1px solid rgba(144,83,246,0.3)",
    mb: 0.5,
  },

  benefitLabel: {
    ...mediumFont,
    color: "#FFFFFF",
    fontSize: { md: getRelativeFontSize(0), xs: getRelativeFontSize(-2) },
    textAlign: "center",
  },

  // Horizontal rule between benefits and headline
  bottomDivider: {
    height: "1px",
    background: "rgba(255,255,255,0.06)",
    mb: { md: 4, xs: 3 },
    position: "relative",
    zIndex: 1,
  },

  bottomCardTitle: {
    ...mediumFont,
    fontSize: { xl: "42px", lg: "36px", md: "30px", sm: "22px", xs: "20px" },
    mb: 1.5,
    background:
      "linear-gradient(130deg, #FFFFFF 20%, #E9D5FF 55%, #9053F6 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    lineHeight: 1.2,
    position: "relative",
    zIndex: 1,
  },

  instructions: {
    ...regularFont,
    color: "rgba(255,255,255,0.46)",
    fontSize: { md: getRelativeFontSize(1), xs: getRelativeFontSize(-1) },
    lineHeight: 1.78,
    mb: 4.5,
    maxWidth: "560px",
    mx: "auto",
    position: "relative",
    zIndex: 1,
  },

  ctaNoCard: {
    ...regularFont,
    color: "rgba(255,255,255,0.28)",
    fontSize: getRelativeFontSize(-2),
    mt: 2,
    letterSpacing: "0.04em",
    position: "relative",
    zIndex: 1,
  },
} as const;

export default homeStyles;
